import { ESCALA, formatarValor } from "../tema";
import { estiloRotulo, montarSeries, opcoesBase } from "./base";

/**
 * Família 2 — barra horizontal de ranking.
 *
 * É o modelo das listas "Maiores Altas / Maiores Quedas" e dos rankings de
 * compradores: uma série só, uma barra deitada por item, o nome do item
 * fora da barra e o valor na ponta.
 *
 * O detalhe que mais aparece nas referências: quando os valores são
 * negativos as barras crescem para a esquerda, e aí o rótulo do valor vai
 * para a esquerda e o nome do item para a direita — os dois trocam de
 * lado. Isso é decidido pelo sinal, não por configuração.
 */
const modelo = {
  id: "barra-ranking",
  label: "Barra de ranking",
  descricao: "Uma barra deitada por item, ordenada. Ex.: Maiores Altas, Maiores Compradores.",

  build(spec) {
    const base = opcoesBase(spec);
    const valores = spec.dados?.series?.[0]?.valores || [];
    const negativo = valores.some((v) => Number(v) < 0);
    const opcoesValor = spec.valores || {};

    return {
      ...base,
      chart: {
        ...base.chart,
        type: "bar",
        // Sem marginLeft/Right fixos: aqui o eixo precisa reservar espaço
        // para o nome de cada item, e uma margem explícita cancela essa
        // reserva — os nomes simplesmente somem para fora da imagem.
        marginLeft: undefined,
        marginRight: 8 * ESCALA,
        marginBottom: 6 * ESCALA,
      },
      xAxis: {
        ...base.xAxis,
        categories: spec.dados?.categorias || [],
        lineWidth: 0,
        // Sem isto o primeiro item da tabela aparece embaixo. Com ele, a
        // ordem digitada é a ordem lida de cima para baixo.
        reversed: true,
        labels: {
          ...base.xAxis.labels,
          // Nas quedas a barra invade a esquerda; o nome do item passa
          // para o outro lado para não ficar por baixo dela.
          align: negativo ? "left" : "right",
          reserveSpace: true,
          x: negativo ? 6 * ESCALA : -6 * ESCALA,
          style: {
            ...base.xAxis.labels.style,
            // Sem isto o Highcharts corta nomes longos com reticências
            // ("ASSAÍ ATACADI…") a partir de uma largura máxima própria.
            textOverflow: "none",
            whiteSpace: "nowrap",
          },
        },
      },
      yAxis: {
        ...base.yAxis,
        reversed: false,
        maxPadding: 0.16,
        minPadding: negativo ? 0.16 : 0,
      },
      plotOptions: {
        ...base.plotOptions,
        bar: {
          borderWidth: 0,
          pointPadding: 0.06,
          groupPadding: 0.04,
          dataLabels: {
            ...estiloRotulo(spec),
            // Sem caixa por padrão: nas listas de altas/quedas o valor fica
            // solto ao lado da barra. Os rankings de estados do USDA usam o
            // valor numa caixa branca — `extras.caixaValor` liga isso.
            ...(spec.extras?.caixaValor
              ? {}
              : { backgroundColor: "transparent", borderWidth: 0 }),
            align: negativo ? "right" : "left",
            inside: false,
            x: negativo ? -4 * ESCALA : 4 * ESCALA,
            formatter() {
              return formatarValor(this.y, opcoesValor);
            },
          },
        },
      },
      // `extras.coresPorItem`: uma cor por barra, como nos rankings de
      // exportação do Comex. Sem isso, a série inteira usa uma cor só.
      series: montarSeries(spec, { showInLegend: false }).map((serie) =>
        spec.extras?.coresPorItem
          ? {
              ...serie,
              colorByPoint: true,
              colors: spec.extras.coresPorItem,
              data: serie.data.map((valor, i) => ({
                y: valor,
                color: spec.extras.coresPorItem[i % spec.extras.coresPorItem.length],
              })),
            }
          : serie
      ),
    };
  },

  exemplo() {
    return {
      modelo: "barra-ranking",
      titulo: [
        { texto: "Bolsa B3", cor: "#FFFFFF" },
        { texto: " - Agroações", cor: "#FFFFFF" },
      ],
      subtitulo: [{ texto: "Maiores Altas (%) - 02/set/26", cor: "#00D0FF" }],
      fonte: "Fonte: RURAL BUSINESS/B3",
      fundo: "#000000",
      fundoPlot: "nenhum",
      dados: {
        categorias: [
          "MAGAZINE LUIZA",
          "AREZZO",
          "VAMOS",
          "ASSAÍ ATACADISTA",
          "LOCALIZA",
          "BANCO DO BRASIL",
        ],
        series: [
          {
            nome: "Alta",
            cor: "#A0E050",
            corFim: "#4A7A20",
            valores: [13.4, 12.7, 12.4, 7.6, 5.8, 5.4],
          },
        ],
      },
      legenda: { visivel: false },
      valores: { decimais: 1, prefixo: "", sufixo: "%", exibirEm: "todos" },
      rotulo: { corTexto: "#FFFFFF", corFundo: "#1A1A1A", corBorda: "#FFFFFF" },
      extras: { formato: "chato" },
    };
  },
};

export default modelo;
